/**
 * Purpose: Build Google Maps URLs that open the native app when installed, else the website.
 * Uses Google's cross-platform Maps URLs (api=1) — same pattern as major listing apps.
 * @see https://developers.google.com/maps/documentation/urls/get-started
 */

export function parseCoordinates(latitude, longitude) {
  const lat = latitude != null && latitude !== '' ? Number(latitude) : NaN;
  const lng = longitude != null && longitude !== '' ? Number(longitude) : NaN;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

export function joinLocationQuery(parts) {
  return parts
    .flatMap((part) => {
      if (part == null || part === '') return [];
      if (typeof part === 'object') {
        return [part.name, part.city, part.address].filter(Boolean);
      }
      return [String(part)];
    })
    .filter(Boolean)
    .join(', ')
    .trim();
}

/**
 * @param {{ latitude?: *, longitude?: *, query?: string }} options
 * @returns {string|null}
 */
export function buildGoogleMapsSearchUrl({ latitude, longitude, query } = {}) {
  const coords = parseCoordinates(latitude, longitude);
  if (coords) {
    const coordinateQuery = `${coords.lat},${coords.lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinateQuery)}`;
  }
  const text = (query || '').trim();
  if (!text) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
}

export function hasGoogleMapsTarget({ latitude, longitude, query, address, city, venue, name } = {}) {
  if (parseCoordinates(latitude, longitude)) return true;
  const text = (query || joinLocationQuery([address, city, venue, name])).trim();
  return Boolean(text);
}

export function resolveGoogleMapsHref(options = {}) {
  const query =
    options.query ||
    joinLocationQuery([options.address, options.city, options.venue, options.name, options.society]);
  return buildGoogleMapsSearchUrl({
    latitude: options.latitude,
    longitude: options.longitude,
    query,
  });
}
